import { useContext, useState } from 'react'
import { Pencil, Check, X } from 'lucide-react'
import {
  GlobalDataContext,
  TemplateContext,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Builder primitive — inline multi-select editor for a row's roles (or any
 * multi-value list of option keys), persisted through decoupled_toolbox's
 * generic write API on the user entity.
 *
 * View mode shows the human label (`displayLocation`) + a pencil. Edit mode
 * opens a checkbox panel built from `optionsLocation` ([{key}], e.g. the role
 * facet buckets) labelled via `labelMapLocation` ({key: label}). Save POSTs
 *
 *   POST /decoupled-api/save?display=decoupled&entity_type=user
 *        &bundle=user&entity_id=<row.id>
 *   body: { data: { roles: [<selected keys>] } }
 *
 * then commits the new value + label into the row. CSRF + credentials are
 * injected by `fetchPatch.js`. The persisted field must be writable in the
 * user `decoupled` view display.
 *
 *   - type: RolesEditor
 *     valueLocation: ~.roles
 *     displayLocation: ~.rolesLabel
 *     optionsLocation: ~~.usersResult.aggregations.by_role.buckets
 *     labelMapLocation: ~~.usersResult.roleLabels
 *     idLocation: ~.id
 *     field: roles
 *     entityType: user
 *     bundle: user
 */
export const RolesEditor = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const resolve = (dataLocation, defaultValue = null) =>
    propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation,
      defaultValue,
      globalDataContext,
      templateContext,
    })

  const { formData: currentValue, formDataPath: valuePath } = resolve(props.valueLocation, [])
  // Optional precomputed display label ; when absent the label is derived from
  // the value + labelMap (ES `render=raw` gives no joined label).
  const hasDisplay = Boolean(props.displayLocation)
  const displayRes = hasDisplay ? resolve(props.displayLocation, '') : { formData: '', formDataPath: null }
  const displayValue = displayRes.formData
  const displayPath = displayRes.formDataPath
  const optionsRaw = resolve(props.optionsLocation, []).formData
  const labelMap = resolve(props.labelMapLocation, {}).formData || {}

  // ES `render=raw` collapses a single-value multi-field to a scalar — normalise
  // the bound value to an array so one-role users behave like multi-role ones.
  const currentRoles = Array.isArray(currentValue)
    ? currentValue
    : (currentValue == null || currentValue === '' ? [] : [currentValue])

  const options = Array.isArray(optionsRaw)
    ? optionsRaw.map((o) => (typeof o === 'string' ? o : o?.key)).filter(Boolean)
    : []

  const field = props.field ?? 'roles'
  const entityType = props.entityType ?? 'user'
  const bundle = props.bundle ?? 'user'
  const display = props.display ?? 'decoupled'
  const saveUrl = props.saveUrl ?? '/decoupled-api/save'
  const empty = props.empty ?? '—'

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState([])
  const [busy, setBusy] = useState(false)

  const labelFor = (key) => labelMap[key] ?? key
  const labelOf = (keys) => (keys.length ? keys.map(labelFor).join(', ') : empty)

  const startEdit = () => {
    setDraft([...currentRoles])
    setEditing(true)
  }

  const cancel = () => setEditing(false)

  const toggle = (key) => {
    setDraft((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  const save = () => {
    const { formData: id } = resolve(props.idLocation ?? '~.id')
    if (id == null) {
      console.error('RolesEditor: missing entity id, cannot save.')
      return
    }

    const url =
      `${saveUrl}?display=${encodeURIComponent(display)}` +
      `&entity_type=${encodeURIComponent(entityType)}` +
      `&bundle=${encodeURIComponent(bundle)}` +
      `&entity_id=${encodeURIComponent(id)}`

    setBusy(true)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { [field]: draft } }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      })
      .then(() => {
        // Commit the new value into the row (the view re-derives its label).
        globalDataContext.updateData([...draft], valuePath)
        if (hasDisplay && displayPath) {
          globalDataContext.updateData(labelOf(draft), displayPath)
        }
        setEditing(false)
      })
      .catch((err) => {
        console.error('RolesEditor: save failed', err)
        // eslint-disable-next-line no-alert
        alert("L'enregistrement des rôles a échoué.")
      })
      .finally(() => setBusy(false))
  }

  if (!editing) {
    const shown = (hasDisplay && displayValue) || labelOf(currentRoles)
    return (
      <span className="group inline-flex items-center gap-1.5 max-w-full">
        <span className="truncate">{shown}</span>
        <button
          type="button"
          onClick={startEdit}
          title="Modifier les rôles"
          className="shrink-0 text-gray-300 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition"
        >
          <Pencil size={14} />
        </button>
      </span>
    )
  }

  return (
    <span className="relative inline-flex items-center gap-1.5">
      <span className="inline-flex flex-col gap-1 bg-white border border-indigo-400 rounded-lg p-2 shadow-lg min-w-44">
        {options.length === 0 && <span className="text-xs text-gray-400 px-1">Aucun rôle</span>}
        {options.map((key) => (
          <label key={key} className="flex items-center gap-2 text-sm px-1 py-0.5 cursor-pointer hover:bg-indigo-50 rounded">
            <input
              type="checkbox"
              checked={draft.includes(key)}
              onChange={() => toggle(key)}
              className="w-4 h-4 rounded border-gray-300 accent-indigo-600"
            />
            <span>{labelFor(key)}</span>
          </label>
        ))}
        <div className="flex items-center justify-end gap-1 pt-1 mt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            title="Enregistrer"
            className="text-green-600 hover:text-green-700 disabled:opacity-40"
          >
            <Check size={16} />
          </button>
          <button
            type="button"
            onClick={cancel}
            title="Annuler"
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        </div>
      </span>
    </span>
  )
}
